import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  CheckCircle2,
  Clock,
  AlertTriangle,
  XCircle,
  Loader2,
  MapPin,
  Calendar,
  User,
  Radio,
} from 'lucide-react';
import { apiGet, apiPost } from '../shared/apiClient';
import { TIPI_EVENTO_AVVISO, type TipoEventoAvviso } from '../shared/constants';

interface ImpiantoRef {
  id: string;
  codice: string;
  nome: string;
  tipo?: string | null;
}

export interface AvvisoPending {
  id: string;
  tipoEvento: TipoEventoAvviso;
  titoloEvento: string;
  descrizioneEvento: string;
  testo: string;
  creatoDa?: string | null;
  inizioAt?: string | null;
  fineAt?: string | null;
  revocatoAt?: string | null;
  createdAt: string;
  timestamp?: string | null;
  impianti: ImpiantoRef[];
  isGenerale: boolean;
}

export function AvvisiModalSquadra() {
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Polling avvisi pendenti da confermare ogni 20 secondi
  const { data: pendingList = [] } = useQuery<AvvisoPending[]>({
    queryKey: ['avvisi-pending-conferme'],
    queryFn: async () => {
      const res = await apiGet<AvvisoPending[]>('/avvisi-impianti/pending-conferme');
      const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      return list;
    },
    refetchInterval: 20000,
    refetchOnWindowFocus: true,
  });

  // Mostra sempre il primo avviso pendente in coda
  const currentAvviso = pendingList.length > 0 ? pendingList[0] : null;

  useEffect(() => {
    if (!currentAvviso) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [currentAvviso]);

  if (!currentAvviso || typeof document === 'undefined') {
    return null;
  }

  const handleConferma = async () => {
    if (!currentAvviso || submitting) return;
    try {
      setSubmitting(true);
      setErrorMessage(null);
      await apiPost(`/avvisi-impianti/${currentAvviso.id}/conferma`, {
        tipoEvento: currentAvviso.tipoEvento,
      });

      // Aggiorna subito la cache escludendo l'avviso confermato
      queryClient.setQueryData<AvvisoPending[]>(['avvisi-pending-conferme'], (old) => {
        if (!old) return [];
        return old.filter((item) => !(item.id === currentAvviso.id && item.tipoEvento === currentAvviso.tipoEvento));
      });

      // Invalida anche la lista degli avvisi attivi per sincronizzare il marquee ticker
      await queryClient.invalidateQueries({ queryKey: ['avvisi-pending-conferme'] });
      await queryClient.invalidateQueries({ queryKey: ['avvisi-impianti-attivi'] });
    } catch (err: any) {
      console.error('Errore durante la conferma lettura avviso:', err);
      const msg = err?.response?.data?.errore || err?.message || 'Errore durante la conferma di lettura';
      setErrorMessage(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Configurazione visuale in base al tipo di evento
  const getEventConfig = (tipo: TipoEventoAvviso) => {
    switch (tipo) {
      case TIPI_EVENTO_AVVISO.IN_VIGORE:
        return {
          icon: <Bell className="w-6 h-6 text-emerald-600 animate-bounce" />,
          badgeBg: 'bg-emerald-100 text-emerald-800 border-emerald-300',
          badgeText: '🟢 ENTRATO IN VIGORE',
          headerBg: 'bg-emerald-50/80 border-emerald-200',
          titleColor: 'text-emerald-950',
          btnBg: 'bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 focus:ring-emerald-500',
        };
      case TIPI_EVENTO_AVVISO.SCADUTO:
        return {
          icon: <Clock className="w-6 h-6 text-amber-600" />,
          badgeBg: 'bg-amber-100 text-amber-900 border-amber-300',
          badgeText: '🟡 AVVISO SCADUTO',
          headerBg: 'bg-amber-50/80 border-amber-200',
          titleColor: 'text-amber-950',
          btnBg: 'bg-amber-600 hover:bg-amber-700 active:bg-amber-800 focus:ring-amber-500',
        };
      case TIPI_EVENTO_AVVISO.REVOCATO:
        return {
          icon: <XCircle className="w-6 h-6 text-rose-600" />,
          badgeBg: 'bg-rose-100 text-rose-900 border-rose-300',
          badgeText: '🔴 AVVISO REVOCATO',
          headerBg: 'bg-rose-50/80 border-rose-200',
          titleColor: 'text-rose-950',
          btnBg: 'bg-rose-600 hover:bg-rose-700 active:bg-rose-800 focus:ring-rose-500',
        };
      default:
        return {
          icon: <AlertTriangle className="w-6 h-6 text-indigo-600" />,
          badgeBg: 'bg-indigo-100 text-indigo-800 border-indigo-300',
          badgeText: 'AVVISO',
          headerBg: 'bg-indigo-50/80 border-indigo-200',
          titleColor: 'text-indigo-950',
          btnBg: 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500',
        };
    }
  };

  const config = getEventConfig(currentAvviso.tipoEvento);
  const totalPending = pendingList.length;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-4 bg-slate-950/75 backdrop-blur-md animate-fade-in safe-bottom"
    >
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[92vh] animate-scale-in">
        {/* Banner superiore con conteggio pendenti se > 1 */}
        {totalPending > 1 && (
          <div className="bg-slate-900 text-white text-[11.5px] font-bold px-4 py-1.5 flex items-center justify-between">
            <span className="flex items-center gap-1.5">
              <Radio className="w-3.5 h-3.5 text-amber-400 animate-pulse" />
              <span>Avvisi di centrale da confermare</span>
            </span>
            <span className="bg-white/20 text-white px-2 py-0.5 rounded-full text-[11px]">
              1 di {totalPending}
            </span>
          </div>
        )}

        {/* Intestazione evento con icona e stato */}
        <div className={`p-4 sm:p-5 border-b flex items-start gap-3.5 ${config.headerBg}`}>
          <div className="p-2.5 rounded-xl bg-white shadow-sm border border-slate-200/80 flex-shrink-0">
            {config.icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-extrabold border tracking-wide uppercase ${config.badgeBg}`}>
                {config.badgeText}
              </span>
            </div>
            <h2 className={`text-base sm:text-lg font-black tracking-tight leading-snug ${config.titleColor}`}>
              {currentAvviso.titoloEvento}
            </h2>
            <p className="text-xs sm:text-[13px] text-slate-600 mt-0.5 font-medium leading-relaxed">
              {currentAvviso.descrizioneEvento}
            </p>
          </div>
        </div>

        {/* Corpo con riassunto dell'avviso */}
        <div className="p-4 sm:p-5 overflow-y-auto space-y-4 flex-1 text-slate-800 text-sm">
          {/* Box testo dell'avviso evidenziato */}
          <div className="p-3.5 sm:p-4 rounded-xl bg-slate-50 border border-slate-200 shadow-inner">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
              Contenuto dell'avviso
            </div>
            <div className="text-sm sm:text-base font-semibold text-slate-900 whitespace-pre-wrap leading-relaxed">
              {currentAvviso.testo}
            </div>
          </div>

          {/* Destinazione / Impianti */}
          <div className="space-y-1.5">
            <div className="text-[11.5px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span>Destinazione / Impianti interessati</span>
            </div>
            {currentAvviso.isGenerale || !currentAvviso.impianti || currentAvviso.impianti.length === 0 ? (
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50 border border-indigo-100 text-indigo-900 font-semibold text-xs">
                📢 Avviso Generale — Valido per tutti gli impianti
              </div>
            ) : (
              <div className="flex flex-col gap-1.5 max-h-44 overflow-y-auto p-2 bg-slate-50 rounded-xl border border-slate-200">
                {currentAvviso.impianti.map((imp) => (
                  <div
                    key={imp.id}
                    className="flex items-start gap-2 p-2 rounded-lg bg-white border border-slate-200 text-slate-800 text-xs font-medium shadow-2xs"
                  >
                    <span className="font-mono font-bold text-slate-900 bg-slate-100 px-1.5 py-0.5 rounded text-[11px] shrink-0">
                      [{imp.codice}]
                    </span>
                    <span className="break-words leading-relaxed flex-1 text-slate-900 font-semibold">
                      {imp.nome}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Dettagli temporali e autore */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-2 border-t border-slate-100 text-xs text-slate-600">
            <div className="flex items-center gap-2 bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-200/60">
              <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <div className="min-w-0">
                <span className="font-semibold text-slate-700 block">Validità:</span>
                <span className="text-[11px] text-slate-500">
                  {currentAvviso.inizioAt ? `Dal ${new Date(currentAvviso.inizioAt).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}` : 'Da subito'}
                  {currentAvviso.fineAt ? ` al ${new Date(currentAvviso.fineAt).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}` : ' (indeterminata)'}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-200/60">
              <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <div className="min-w-0">
                <span className="font-semibold text-slate-700 block">Inserito da:</span>
                <span className="text-[11px] text-slate-500 truncate block">
                  {currentAvviso.creatoDa || 'Centrale Operativa'}
                </span>
              </div>
            </div>
          </div>

          {/* Errore eventuale */}
          {errorMessage && (
            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs font-medium flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 text-rose-600" />
              <span>{errorMessage}</span>
            </div>
          )}
        </div>

        {/* Footer con bottone di conferma obbligatoria */}
        <div className="p-4 sm:p-5 bg-slate-50 border-t border-slate-200 flex flex-col gap-2">
          <button
            type="button"
            onClick={handleConferma}
            disabled={submitting}
            className={`w-full py-3.5 px-4 rounded-xl text-white font-bold text-sm sm:text-base flex items-center justify-center gap-2 shadow-md active:scale-[0.99] transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${config.btnBg}`}
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Registrazione conferma...</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5" />
                <span>Ho preso visione e confermo lettura</span>
              </>
            )}
          </button>
          <div className="text-center text-[11px] text-slate-500 font-medium">
            La conferma di lettura viene registrata nel sistema a nome della tua squadra.
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
